import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { changeProgramType } from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { getAvailableProgramTypes } from 'partner360/helpers/offerings/getAvailableProgramTypes'
import { getOfferingsInfo } from 'partner360/helpers/offerings/getOfferingsInfo'

import { getNextIncompleteOffering } from '../../lib/getNextIncompleteOffering'
import { getOfferingTitleKey } from '../../lib/getOfferingTitleKey'
import { setCockpitOffering } from '../financialCockpitSlice'
import {
  selectCockpitFirstPartnerOfferingsCatalog,
  selectCockpitPartnerCompleteness,
} from '../selectors'

export interface INextIncompleteOfferingLink {
  offering: string
  percent: number
  /** The offering's tab title, as the cockpit's tabs name it. */
  titleKey: string
  /** Moves the cockpit to the offering, as its tab does. */
  open: () => void
}

/**
 * Where a signature the server holds for another offering's plan leads: the next enabled offering,
 * in tab order, whose plan is short of 100%. `null` when the offering is held by its own sections
 * or every other plan is complete.
 */
export const useNextIncompleteOffering = (
  offering: string | null
): INextIncompleteOfferingLink | null => {
  const dispatch = useAppDispatch()
  const offeringsCatalog = useSelector(selectCockpitFirstPartnerOfferingsCatalog)
  const profiles = useSelector(selectCockpitPartnerCompleteness)

  const next = useMemo(() => {
    const ordered = [...getAvailableProgramTypes(offeringsCatalog)].sort(
      (a, b) => a.orderBy - b.orderBy
    )
    const offerings = getOfferingsInfo(ordered)
      .enabledOfferings.map(({ programType }) => programType as string)
      .filter((programType) => programType !== ProgramTypes.Overall)

    return getNextIncompleteOffering({ offerings, current: offering, profiles })
  }, [offering, offeringsCatalog, profiles])

  const nextOffering = next?.offering

  const open = useCallback(() => {
    if (!nextOffering) {
      return
    }

    dispatch(changeProgramType(nextOffering as ProgramTypes))
    dispatch(setCockpitOffering(nextOffering as ProgramTypes))
  }, [dispatch, nextOffering])

  return useMemo(
    () => (next ? { ...next, titleKey: getOfferingTitleKey(next.offering), open } : null),
    [next, open]
  )
}
